// Native forced-roll decisions and cast-time status snapshots, shared by status application, zones and skill rolls.
import { type JanusCtx, type Row, activeStatuses, ecId, handBack, itemField, record, upper } from '../context.js';
import { layerListEntryUuid } from './status-trigger-gate.js';
import { statusRows, statusItemId } from '../../statuses/expiry.js';
import { statusScalar } from '../../actions/activity.js';

const DOT_ROLL = '77A044B7-7041-CA96-3C63-BB718B15A364';
const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';

/** A wrapper list reads its [0] (uncut), a structured entry
 * reads layer_list_entry_uuid() (item_layer_effect → items.item_id → item_id, each cut at '|'), a bare string is compared whole. */
export function forcedEntryId(entry: unknown): string {
  if (Array.isArray(entry)) return entry.length && entry[0] !== undefined && entry[0] !== null ? upper(entry[0]) : layerListEntryUuid(entry);
  if (record(entry)) return layerListEntryUuid(entry);
  return typeof entry === 'string' ? upper(entry) : '';
}
export function carrierNamesRollType(ctx: JanusCtx, carrier: Row, key: string, rollTypes: ReadonlySet<string>): boolean {
  return ctx.read.entries(carrier, key).some((entry) => rollTypes.has(forcedEntryId(entry)));
}

/** A saved tick extreme replaces only this caster's root verdict for its roll type.
 * Keep the held creature intact; live status/target modifiers still use the normal max-wins reader. */
export function withStatusTickExtreme<T>(ctx: JanusCtx, status: Row, definition: Row, caster: Row, work: () => T): T {
  const forced = Number(statusScalar(status, 'status_forced_roll_extreme') ?? 0);
  if (!forced) return work();
  const prior = ctx.read, casterId = ecId(caster);
  const tick = upper(prior.value(definition, 'roll_type')) === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL;
  const side = forced > 0 ? 'roll_type_is_rolled_at_max' : 'roll_type_is_rolled_at_min';
  ctx.read = { ...prior, entries: (row, key) => {
    const entries = prior.entries(row, key);
    if (!row || ecId(row) !== casterId || !['roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min'].includes(key)) return entries;
    const kept = entries.filter(entry => forcedEntryId(entry) !== tick);
    return key === side ? [...kept, tick] : kept;
  } };
  try { return work(); } finally { ctx.read = prior; }
}

/** The caster's own layers, then each held status (max wins). */
export function casterRollTypeExtreme(ctx: JanusCtx, caster: Row, rollType: string, reason: string): 'max' | 'min' | null {
  const types = new Set([upper(rollType)]);
  let max = carrierNamesRollType(ctx, caster, 'roll_type_is_rolled_at_max', types);
  let min = carrierNamesRollType(ctx, caster, 'roll_type_is_rolled_at_min', types);
  for (const status of activeStatuses(ctx, caster)) {
    const inline = record(status.status_arr);
    const def = inline && Object.keys(inline).some((key) => key !== 'items') ? inline : ctx.read.definition(upper(status.status_item_id));
    if (!def) handBack(reason);
    max ||= carrierNamesRollType(ctx, def, 'roll_type_is_rolled_at_max', types);
    min ||= carrierNamesRollType(ctx, def, 'roll_type_is_rolled_at_min', types);
  }
  return max ? 'max' : min ? 'min' : null;
}

/**
 * The cast-time snapshot for one new instance: re-ticking = a DoT roll type, a contest retry, or an
 * instance carrying an area (status_aoe_type); the value is this dispatch's stash for the tick's roll type (an explicit null wins),
 * else a live caster resolve. 1 = max, -1 = min, 0 = none. A same-item sibling on the SAME connection shares the value (this use mints
 * its connection; late entrants reuse the held connection's original value).
 */
export function statusForcedRollExtreme(ctx: JanusCtx, statusDefinition: Row, caster: Row, instanceAoeType: unknown): -1 | 0 | 1 {
  const rollType = upper(ctx.read.value(statusDefinition, 'roll_type'));
  const reticks = rollType === DOT_ROLL || ctx.read.has(statusDefinition, 'target_retries_contesting_roll_on_end_of_turn')
    || (instanceAoeType !== null && instanceAoeType !== undefined && instanceAoeType !== '');
  if (!reticks) return 0;
  // A late entrant inherits the connection's cast-time snapshot, including a lean omitted zero.
  const connection = upper(ctx.statusConnectedId);
  const itemId = upper(itemField(statusDefinition, 'item_id'));
  if (connection && itemId) {
    const siblings: Row[] = [...ctx.statusesAdded as unknown as Row[],
      ...[ctx.encounter, ...ctx.members.values()].flatMap(row => statusRows(row, 'target'))];
    const sibling = siblings.find(row => upper(statusScalar(row, 'status_connected_id')) === connection && statusItemId(row) === itemId);
    if (sibling) {
      const value = Number(statusScalar(sibling, 'status_forced_roll_extreme') ?? 0);
      return value > 0 ? 1 : value < 0 ? -1 : 0;
    }
  }
  const tickRollType = rollType === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL;
  const extreme = ctx.castForcedRollExtreme && Object.prototype.hasOwnProperty.call(ctx.castForcedRollExtreme, tickRollType)
    ? ctx.castForcedRollExtreme[tickRollType]
    : casterRollTypeExtreme(ctx, caster, tickRollType, 'apply_status_effect:forced-extreme-status-definition-not-held');
  return extreme === 'max' ? 1 : extreme === 'min' ? -1 : 0;
}
